import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    CategoryModel,
    ConfigConstants,
    InternalInfoClosePopupModel,
    InternalInfoModel,
    ModalClass,
    OdataNotificationService,
    RESULT_TYPES,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { InternalInfoClosePopupComponent } from './internal-info-close-popup/internal-info-close-popup.component';
import INTERNAL_INFO_CATEGORY = ConfigConstants.INTERNAL_INFO_CATEGORY;

@Component({
    selector: 'pace-internal-info',
    templateUrl: './internal-info.component.html',
    styleUrls: ['./internal-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalInfoComponent implements OnInit, OnDestroy {
    @Input()
    public model: InternalInfoModel;

    public modalResult = new Subject<boolean>();
    public INTERNAL_INFO_CATEGORY = ConfigConstants.INTERNAL_INFO_CATEGORY;
    public internalInfoGroup: FormGroup;
    public contactGroup: FormGroup;
    public contactChildren: FormArray;
    public parkingGroup: FormGroup;
    public parkingChildren: FormArray;
    public technicalSpecGroup: FormGroup;
    public technicalSpecChildren: FormArray;
    public organisationGroup: FormGroup;
    public organisationChildren: FormArray;
    public constancyCheckGroup: FormGroup;
    public constancyCheckChildren: FormArray;
    public dispatchGroup: FormGroup;
    public dispatchChildren: FormArray;
    public subSystemGroup: FormGroup;
    public subSystemChildren: FormArray;

    private destroy$ = new Subject<void>();
    public isEdited = false;
    public isLoading = false;
    public hasUnsubmittedChanges = false;

    public contactModelValues: CategoryModel;
    public parkingModelValues: CategoryModel;
    public technicalSpecModelValues: CategoryModel;
    public organisationModelValues: CategoryModel;
    public constancyCheckModelValues: CategoryModel;
    public dispatchModelValues: CategoryModel;
    public subSystemModelValues: CategoryModel;
    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private modalService: BsModalService,
        private readonly odataNotificationService: OdataNotificationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        this.destroy$ = new Subject<void>();
    }

    public ngOnInit(): void {
        this.contactModelValues = this.model.categoryModel.getSelectedCategory(INTERNAL_INFO_CATEGORY.CONTACT);
        this.parkingModelValues = this.model.categoryModel.getSelectedCategory(INTERNAL_INFO_CATEGORY.PARKING);
        this.technicalSpecModelValues = this.model.categoryModel.getSelectedCategory(
            INTERNAL_INFO_CATEGORY.TECHNICAL_SPEC,
        );
        this.organisationModelValues = this.model.categoryModel.getSelectedCategory(INTERNAL_INFO_CATEGORY.ORG_MATTERS);
        this.constancyCheckModelValues = this.model.categoryModel.getSelectedCategory(
            INTERNAL_INFO_CATEGORY.CONSTANCY_CHECK,
        );
        this.dispatchModelValues = this.model.categoryModel.getSelectedCategory(INTERNAL_INFO_CATEGORY.DISPATCH);
        this.subSystemModelValues = this.model.categoryModel.getSelectedCategory(INTERNAL_INFO_CATEGORY.SUB_SYSTEMS);

        this.contactChildren = this.formBuilder.array([]);
        this.contactGroup = this.formBuilder.group({
            contactChildren: this.contactChildren,
        });
        this.parkingChildren = this.formBuilder.array([]);
        this.parkingGroup = this.formBuilder.group({
            parkingChildren: this.parkingChildren,
        });
        this.technicalSpecChildren = this.formBuilder.array([]);
        this.technicalSpecGroup = this.formBuilder.group({
            technicalSpecChildren: this.technicalSpecChildren,
        });
        this.organisationChildren = this.formBuilder.array([]);
        this.organisationGroup = this.formBuilder.group({
            organisationChildren: this.organisationChildren,
        });
        this.constancyCheckChildren = this.formBuilder.array([]);
        this.constancyCheckGroup = this.formBuilder.group({
            constancyCheckChildren: this.constancyCheckChildren,
        });
        this.dispatchChildren = this.formBuilder.array([]);
        this.dispatchGroup = this.formBuilder.group({
            dispatchChildren: this.dispatchChildren,
        });
        this.subSystemChildren = this.formBuilder.array([]);
        this.subSystemGroup = this.formBuilder.group({
            subSystemChildren: this.subSystemChildren,
        });

        this.internalInfoGroup = this.formBuilder.group({
            contact: this.contactGroup,
            parking: this.parkingGroup,
            technicalSpec: this.technicalSpecGroup,
            organisation: this.organisationGroup,
            constancyCheck: this.constancyCheckGroup,
            dispatch: this.dispatchGroup,
            subSystem: this.subSystemGroup,
        });

        this.internalInfoGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.isEdited = this.internalInfoGroup.dirty;
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public save(): void {
        this.isEdited = false;
        this.hasUnsubmittedChanges = true;
        // setting isEdited to enable Add button once last edited contact is saved
        this.contactModelValues.isEdited = false;
    }

    public submitInternalInfo(): void {
        this.isLoading = true;

        this.model.updateHeaderInfoIfValueIsChanged();

        const editedCategories = this.model.categoryModel.categoryArray.filter((category) => {
            return category.isEdited;
        });

        const submitCategories$ = editedCategories.map((category) =>
            this.odataNotificationService.sendInternalInfoModel(category, this.model.notificationId).pipe(
                tap(() => {
                    category.isEdited = false;
                    category.isContentAvailable = category.textLine.value.length > 0;
                    // update originalCategoryModelList with the submitted value
                    this.model.originalCategoryModel = _.cloneDeep(this.model.categoryModel);
                }),
            ),
        );
        const isContactValueChanged = this.model.isContactValuesChanged();

        const submitContacts$ = isContactValueChanged
            ? this.odataNotificationService.sendInternalInfoModel(this.model, this.model.notificationId).pipe(
                  tap(() => {
                      this.model.categoryModel.contact.isContentAvailable = !!this.model.contactModel.value.length;
                      // update originalContactModelList with the submitted value
                      this.model.originalContactModelList = _.cloneDeep(this.model.contactModel.value);
                  }),
              )
            : of(undefined);

        forkJoin([...submitCategories$, submitContacts$])
            .pipe(
                catchError((error) => {
                    this.log.error('InternalInfoComponent : submitInternalInfo : ', JSON.stringify(error));
                    return of(undefined);
                }),
                finalize(() => {
                    this.isLoading = false;
                    this.modalResult.next(true);
                    this.modalInstance.hide();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public onClose(): void {
        // If user has not edited anything, then just close the popup else show warning popup with below action buttons
        if (!this.model.isCategoryValuesChanged() && !this.model.isContactValuesChanged()) {
            this.modalInstance.hide();
            return;
        }
        const invalidCategories = this.model.categoryModel
            .getAllCategoriesModel()
            .filter((category) => category.isInvalid)
            .map((filteredCategory) => filteredCategory.title)
            .toString();
        // Yes  : Send and close popup
        // No   : Discard changes and close popup
        // Back : Keep changes and do not close popup
        // If any category is invalid then "Yes" button will be hidden as user has to clear before submitting to backend
        let title = <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_TITLE');
        let invalidMessage = '';
        if (invalidCategories.length > 0) {
            title = <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_TITLE_FOR_INVALID_ENTRIES');
            invalidMessage = `${<string>(
                this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_BODY_FOR_INVALID_ENTRIES', { invalidCategories })
            )}<br>`;
        }

        const modalInstance = this.modalService.show(InternalInfoClosePopupComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new InternalInfoClosePopupModel(
                    title,
                    <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_BODY', { invalidMessage }),
                    <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_YES_LABEL'),
                    <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_NO_LABEL'),
                    <string>this.translate.instant('INTERNAL_INFO_CLOSE_MODAL_BACK_LABEL'),
                    !invalidCategories.length,
                ),
            },
            ariaLabelledBy: 'internal-info-close-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                take(1),
                tap((result: RESULT_TYPES) => {
                    if (result === RESULT_TYPES.BACK) {
                        return;
                    }
                    if (result === RESULT_TYPES.YES) {
                        // delete values which are un-valid
                        this.model.deleteInvalidContactsFromList();
                        this.submitInternalInfo();
                        this.changeDetectorRef.detectChanges();
                    } else {
                        this.model.restoreOriginalContactsValues();
                        this.model.restoreOriginalCategoryValues();
                        this.isEdited = false;
                        this.modalResult.next(false);
                        this.modalInstance.hide();
                    }
                }),
            )
            .subscribe();
    }
}
